import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, Overline } from "@/components/Reveal";
import { IMAGES, FOUNDER_BIO } from "@/data";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <>
      {/* About Brahmsara */}
      <section id="about" data-testid="about-brahmsara-section" className="section-pad py-24 md:py-36">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5 relative">
            <div className="relative overflow-hidden clip-arch grain">
              <img src={IMAGES.architecture} alt="Elegant minimal architecture with soft light" className="w-full h-[440px] md:h-[560px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-4 md:right-6 bg-[var(--panel)] border border-[rgba(200,149,54,0.3)] px-7 py-5 shadow-[0_20px_60px_-30px_rgba(200,149,54,0.6)]">
              <p className="font-serif-display text-4xl text-[var(--gold)]">Brahm&middot;sara</p>
              <p className="font-sans-ui text-xs text-[var(--muted-ink)] tracking-wide mt-1">the essence of the infinite</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Overline>About Brahmsara</Overline>
            <Reveal>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-6 mb-8">
                A sanctuary for clarity, healing and the quiet return to yourself.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-sans-ui text-[var(--muted-ink)] text-base md:text-lg leading-relaxed font-light max-w-2xl">
                Brahmsara is a trusted spiritual guidance and healing practice that helps people gain clarity, understand recurring life patterns, and reconnect with themselves through contemplative practices. It blends ancient wisdom with a calm, modern presentation — timeless, sincere and deeply human.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="font-sans-ui text-[var(--muted-ink)] text-base md:text-lg leading-relaxed font-light max-w-2xl mt-5">
                Here, spirituality is offered not as spectacle but as gentle support — a safe, non-judgemental space to explore life&rsquo;s deeper questions, release what no longer serves, and move forward with confidence.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* About Shubham Jain */}
      <section id="founder" ref={ref} data-testid="about-founder-section" className="noir grain py-24 md:py-36 overflow-hidden">
        <div className="gold-glow" />
        <div className="section-pad relative z-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Overline>About Shubham Jain</Overline>
            <Reveal>
              <h2 className="font-serif-display text-[var(--cream)] text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-6 mb-8">
                A guide, healer and storyteller of the <span className="text-[var(--gold-bright)] italic">soul&rsquo;s journey.</span>
              </h2>
            </Reveal>
            <div className="space-y-5 max-w-2xl">
              {FOUNDER_BIO.map((p, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <p className="font-sans-ui text-[var(--cream)]/70 text-[0.98rem] md:text-base leading-relaxed font-light">
                    {p}
                  </p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="flex flex-wrap gap-3 mt-9">
                {["Akashic Practitioner", "Reiki Healer", "Tarot Reader", "Author", "Meditation Guide"].map((t) => (
                  <span key={t} className="font-sans-ui text-xs tracking-wide px-4 py-2 border border-[rgba(233,172,70,0.4)] rounded-full text-[var(--cream)]">
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <motion.div style={{ y }} className="relative overflow-hidden">
              <img src={IMAGES.founder} alt="Portrait of Shubham Jain" className="w-full h-[480px] md:h-[640px] object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--noir)] via-transparent to-transparent opacity-70" />
              <div className="absolute inset-0 ring-1 ring-inset ring-[rgba(233,172,70,0.4)]" />
            </motion.div>
            <p className="font-serif-display italic text-2xl mt-5 text-[var(--gold-bright)]">Shubham Jain</p>
            <p className="font-sans-ui text-xs tracking-wide text-[var(--cream)]/60">Founder, Brahmsara</p>
          </div>
        </div>
      </section>
    </>
  );
}
